import React, { useMemo } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import {
  DUE_TYPES,
  createInvoicePaymentPlanRules,
  isInvoicePaymentPlanRules,
  isPlatformDefaultPaymentRules,
  normalizeCustomPaymentScheduleRules,
  validatePaymentScheduleRules,
  resolvePaymentTerms,
  rulesForPaymentCalculation,
} from '../../../utils/paymentTerms';

const dueOptions = (dueNowLabel) => [
  { value: DUE_TYPES.NOW, label: dueNowLabel },
  { value: DUE_TYPES.SPAN_PERCENT, label: 'Midway through booking period' },
  { value: DUE_TYPES.DAYS_BEFORE_EVENT, label: 'Days before event' },
];

const sampleEventDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().slice(0, 10);
};

/**
 * Simplified installment editor for quote invoices.
 * Emits payment_schedule_rules JSON (fixed single-window plan) or null for full pay.
 */
const InvoicePaymentPlanEditor = ({
  value,
  onChange,
  sampleAmount = 0,
  title = 'Payment plan',
  description = 'Split the quote into installments the client can pay after they accept.',
  offHint = 'Client pays the full invoice total after accepting (plus any platform service charge).',
  dueNowLabel = 'When client accepts',
}) => {
  const enabled = Boolean(value) && !isPlatformDefaultPaymentRules(value);
  const rules = useMemo(() => {
    if (!enabled) return createInvoicePaymentPlanRules();
    return normalizeCustomPaymentScheduleRules(
      isInvoicePaymentPlanRules(value) ? value : createInvoicePaymentPlanRules(value?.windows?.[0]?.installments)
    );
  }, [enabled, value]);

  const installments = rules.windows?.[0]?.installments || [];
  const validation = useMemo(
    () => (enabled ? validatePaymentScheduleRules(rules) : { valid: true, errors: [] }),
    [enabled, rules]
  );

  const preview = useMemo(() => {
    if (!enabled || sampleAmount <= 0) return null;
    return resolvePaymentTerms(
      sampleEventDate(),
      sampleAmount,
      rulesForPaymentCalculation(rules),
      { platformFee: 0 }
    );
  }, [enabled, rules, sampleAmount]);

  const emitPlan = (nextInstallments) => {
    onChange(createInvoicePaymentPlanRules(nextInstallments));
  };

  const setEnabled = (on) => {
    if (!on) {
      onChange(null);
      return;
    }
    onChange(createInvoicePaymentPlanRules(installments));
  };

  const updateRow = (index, patch) => {
    emitPlan(
      installments.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        if (patch.dueType != null) {
          const type = patch.dueType;
          if (type === DUE_TYPES.DAYS_BEFORE_EVENT) {
            next.due = { type, days: row.due?.days ?? 14 };
          } else if (type === DUE_TYPES.SPAN_PERCENT) {
            next.due = { type, percent: row.due?.percent ?? 50 };
          } else {
            next.due = { type: DUE_TYPES.NOW };
          }
          delete next.dueType;
        }
        return next;
      })
    );
  };

  const addRow = () => {
    emitPlan([
      ...installments,
      { label: 'Payment', percentage: 0, due: { type: DUE_TYPES.NOW } },
    ]);
  };

  const removeRow = (index) => {
    if (installments.length <= 1) return;
    emitPlan(installments.filter((_, i) => i !== index));
  };

  const percentTotal = installments.reduce((s, r) => s + (parseFloat(r.percentage) || 0), 0);
  const dueSelectOptions = dueOptions(dueNowLabel);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ink">{title}</p>
          <p className="text-[11px] text-ink-tertiary mt-0.5">{description}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-medium text-ink cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-border"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Custom installments
        </label>
      </div>

      {!enabled ? (
        <p className="text-xs text-ink-secondary rounded-md bg-surface-muted px-2.5 py-2">
          {offHint}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            {installments.map((row, index) => {
              const dueType = row.due?.type || DUE_TYPES.NOW;
              return (
                <div
                  key={`inst-${index}`}
                  className="grid grid-cols-1 gap-2 rounded-md border border-border bg-canvas p-2.5 sm:grid-cols-12 sm:items-end"
                >
                  <div className="sm:col-span-3">
                    <Input
                      label={index === 0 ? 'Label' : undefined}
                      value={row.label || ''}
                      onChange={(e) => updateRow(index, { label: e.target.value })}
                      placeholder="Deposit"
                      className="!text-xs"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Input
                      label={index === 0 ? '%' : undefined}
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={row.percentage ?? ''}
                      onChange={(e) =>
                        updateRow(index, { percentage: parseFloat(e.target.value) || 0 })
                      }
                      className="!text-xs"
                    />
                  </div>
                  <div className="sm:col-span-4">
                    <CustomSelect
                      label={index === 0 ? 'Due' : undefined}
                      value={dueType}
                      onChange={(v) => updateRow(index, { dueType: v })}
                      options={dueSelectOptions}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    {dueType === DUE_TYPES.DAYS_BEFORE_EVENT ? (
                      <Input
                        label={index === 0 ? 'Days' : undefined}
                        type="number"
                        min="0"
                        value={row.due?.days ?? 14}
                        onChange={(e) =>
                          updateRow(index, {
                            due: {
                              type: DUE_TYPES.DAYS_BEFORE_EVENT,
                              days: Math.max(0, parseInt(e.target.value, 10) || 0),
                            },
                          })
                        }
                        className="!text-xs"
                      />
                    ) : dueType === DUE_TYPES.SPAN_PERCENT ? (
                      <Input
                        label={index === 0 ? 'At %' : undefined}
                        type="number"
                        min="0"
                        max="100"
                        value={row.due?.percent ?? 50}
                        onChange={(e) =>
                          updateRow(index, {
                            due: {
                              type: DUE_TYPES.SPAN_PERCENT,
                              percent: Math.max(
                                0,
                                Math.min(100, parseFloat(e.target.value) || 0)
                              ),
                            },
                          })
                        }
                        className="!text-xs"
                      />
                    ) : (
                      <p className="text-[11px] text-ink-tertiary pb-2.5">On accept</p>
                    )}
                  </div>
                  <div className="sm:col-span-1 flex sm:justify-end pb-0.5">
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      disabled={installments.length <= 1}
                      className="rounded-md p-1.5 text-ink-tertiary hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button type="button" size="sm" variant="outline" icon={Plus} onClick={addRow}>
              Add installment
            </Button>
            <span
              className={`text-xs font-medium tabular-nums ${
                Math.abs(percentTotal - 100) < 0.01 ? 'text-emerald-700' : 'text-amber-700'
              }`}
            >
              Total {Math.round(percentTotal * 100) / 100}%
            </span>
          </div>

          {!validation.valid && (
            <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <ul className="space-y-0.5 list-disc pl-3">
                {validation.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {preview?.schedule?.length ? (
            <div className="rounded-md border border-border bg-surface-muted px-2.5 py-2 space-y-1">
              <p className="text-[11px] font-semibold text-ink">Preview (example event in 90 days)</p>
              {preview.schedule.map((row) => (
                <div
                  key={`${row.label}-${row.index}`}
                  className="flex justify-between gap-2 text-[11px] text-ink-secondary"
                >
                  <span>
                    {row.label} · {row.percentage}% · {row.dueDate}
                  </span>
                  <span className="tabular-nums font-medium text-ink">
                    ${Number(row.totalAmount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default InvoicePaymentPlanEditor;
